import React from 'react';
import { useLang } from '../context/LanguageContext';


export default function Hero() {
  const { t, lang } = useLang();
  const h = t.hero;
  const isAr = lang === 'ar';

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden bg-misky-white noise">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-misky-silver opacity-60 blur-3xl" />
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-gray-200 opacity-50 blur-2xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div className="animate-slide-up">
            <div className="inline-flex items-center gap-2 border border-misky-black/20 rounded-full px-4 py-1.5 mb-8">
              <div className="w-1.5 h-1.5 rounded-full bg-misky-black" />
              <span className={`text-xs font-medium text-misky-gray ${isAr ? 'font-arabic' : 'tracking-[0.1em]'}`}>{h.tag}</span>
            </div>

            <h1 className="font-bold leading-none mb-3">
              <span className="font-display block text-7xl sm:text-8xl lg:text-9xl text-misky-black tracking-tight">MISKY</span>
              <span className={`block text-lg sm:text-xl text-misky-mid mt-2 ${isAr ? 'font-arabic font-bold' : 'tracking-[0.2em] uppercase font-display'}`}>
                {h.tagline}
              </span>
            </h1>

            <p className={`text-xl font-semibold text-misky-gray mb-5 ${isAr ? 'font-arabic' : ''}`}>{h.subtitle}</p>

            <p className="text-misky-gray leading-relaxed mb-8 max-w-md text-sm">{h.description}</p>

            <div className="flex flex-wrap gap-2 mb-10">
              {h.badges.map((b) => (
                <span key={b} className="text-xs border border-misky-black/20 text-misky-gray px-3 py-1 rounded-full">{b}</span>
              ))}
            </div>

            <div className="flex items-center gap-6">
              <div>
                <span className="font-display text-4xl font-bold text-misky-black">{h.price}</span>
                <span className="text-misky-mid ml-1 font-medium text-lg">{h.currency}</span>
                <div className="text-xs text-misky-light mt-0.5">{h.capacity}</div>
              </div>
              <button
                onClick={() => document.getElementById('order')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex-1 sm:flex-none bg-misky-black text-white px-8 py-4 rounded-full font-semibold hover:bg-misky-dark transition-colors shadow-lg text-sm tracking-wide"
              >
                {h.cta}
              </button>
            </div>
          </div>

          {/* Visual */}
          <div className="flex justify-center items-center animate-fade-in">
            <div className="relative">
              <div className="w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-gradient-to-br from-white to-misky-silver border border-misky-silver shadow-2xl flex items-center justify-center animate-float">
                <div className="w-52 h-52 sm:w-64 sm:h-64 rounded-full bg-white border-2 border-misky-silver shadow-inner flex flex-col items-center justify-center gap-3 p-6">
                  <div className="relative w-24 h-16">
                    <div className="absolute inset-0 bg-gradient-to-b from-white to-gray-200 shadow-md" style={{ borderRadius: '60% 40% 60% 40% / 50% 50% 50% 50%' }} />
                    <div className="absolute top-1 left-2 right-2 h-2 bg-white/70 rounded-full blur-sm" />
                  </div>
                  <div className="font-display text-2xl font-bold text-misky-black tracking-widest">MISKY</div>
                  <div className="text-[9px] tracking-[0.15em] text-misky-mid uppercase text-center">Face & Body Cream</div>
                  <div className="text-[8px] tracking-[0.1em] text-misky-light">150 ml</div>
                </div>
              </div>

              <div className="absolute -top-6 -left-4 bg-white border border-gray-100 rounded-2xl shadow-lg p-3 animate-slide-up">
                <div className="text-xs font-bold text-misky-black">{h.ceramide}</div>
                <div className="text-[10px] text-misky-mid">{h.barrierRepair}</div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-misky-black text-white rounded-2xl shadow-lg p-3 animate-slide-up">
                <div className="text-xs font-bold">150 ml</div>
                <div className="text-[10px] text-misky-light">{h.dailyFormula}</div>
              </div>

              <div className="absolute top-1/2 -right-12 bg-white border border-gray-100 rounded-xl shadow-md p-2.5 animate-fade-in">
                <div className="text-[10px] font-bold text-misky-black">🌿 Aloe Vera</div>
                <div className="text-[9px] text-misky-mid">Extract</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-5 h-8 border-2 border-misky-black/20 rounded-full flex items-start justify-center pt-1">
          <div className="w-1 h-2 bg-misky-black/40 rounded-full" />
        </div>
      </div>
    </section>
  );
}
